import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Target, Calendar, Plus, ChevronRight, Pencil, Copy,
  Archive, Trash2, Check, X, Flag, ListChecks, Sparkles, RefreshCw,
} from 'lucide-react';
import { T, ROLES, HEALTH, GOAL_PRIORITY, springs } from '../lib/tokens';
import { supabase } from '../lib/supabase';
import { apiFetch } from '../lib/api';
import { fmtDate, fmtDeadline, pct, fmt, goalHealth } from '../lib/helpers';
import { Glass } from '../components/glass/Glass';
import { Btn } from '../components/primitives/Btn';
import { RoleBadge, HealthBadge, Pill } from '../components/primitives/Badge';
import { ProgressBar } from '../components/primitives/ProgressBar';
import { Md } from '../components/primitives/Md';
import { ViewHero } from '../components/cards/ViewHero';

// GoalDetailPanel
const GoalDetailPanel = ({ goal, milestones, tasks, onClose, onUpdate }) => {
  const [editTitle, setEditTitle] = useState(goal.title);
  const [editDesc, setEditDesc] = useState(goal.description || '');
  const [editDeadline, setEditDeadline] = useState(goal.deadline ? goal.deadline.split('T')[0] : '');
  const [editMetric, setEditMetric] = useState(goal.metric_current ?? 0);
  const [editPriority, setEditPriority] = useState(goal.priority || 3);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [newMilestoneTitle, setNewMilestoneTitle] = useState('');
  const [addingMilestone, setAddingMilestone] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskMilestoneId, setNewTaskMilestoneId] = useState(null);
  const [addingTask, setAddingTask] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [editMilestoneTitle, setEditMilestoneTitle] = useState('');
  const [editingMilestoneDate, setEditingMilestoneDate] = useState(null);
  const [editingTaskDate, setEditingTaskDate] = useState(null);

  const r = ROLES[goal.roles?.slug];
  const goalMilestones = milestones.filter(m => m.goal_id === goal.id).sort((a, b) => a.sort_order - b.sort_order);
  const goalTasks = tasks.filter(t => t.goal_id === goal.id);
  const doneMilestones = goalMilestones.filter(m => m.done).length;
  const progress = goal.type === 'quantitative'
    ? pct(editMetric, goal.metric_target)
    : goalMilestones.length > 0 ? Math.round((doneMilestones / goalMilestones.length) * 100) : 0;
  const health = goalHealth(goal, milestones);
  const h = health ? HEALTH[health] : null;

  // Group tasks: under milestones or directly under goal
  const tasksByMilestone = {};
  const orphanTasks = [];
  goalTasks.forEach(t => {
    if (t.milestone_id) {
      (tasksByMilestone[t.milestone_id] = tasksByMilestone[t.milestone_id] || []).push(t);
    } else {
      orphanTasks.push(t);
    }
  });

  const saveGoal = async () => {
    setSaving(true);
    const updates = { title: editTitle, description: editDesc || null, priority: editPriority };
    if (editDeadline) updates.deadline = editDeadline;
    else updates.deadline = null;
    if (goal.type === 'quantitative') updates.metric_current = Number(editMetric);
    await supabase.from('goals').update(updates).eq('id', goal.id);
    // Trigger conflict detection when priority changes
    if (editPriority !== goal.priority) {
      try { await apiFetch('/conflicts/detect', { method: 'POST' }); } catch {}
    }
    setIsEditing(false);
    setSaving(false);
    onUpdate();
  };

  const cancelEdit = () => {
    setEditTitle(goal.title);
    setEditDesc(goal.description || '');
    setEditDeadline(goal.deadline ? goal.deadline.split('T')[0] : '');
    setEditMetric(goal.metric_current ?? 0);
    setEditPriority(goal.priority || 3);
    setIsEditing(false);
  };

  const toggleMilestone = async (m) => {
    await supabase.from('milestones').update({ done: !m.done }).eq('id', m.id);
    onUpdate();
  };

  const toggleTask = async (t) => {
    const newDone = !t.done;
    await supabase.from('tasks').update({ done: newDone, done_at: newDone ? new Date().toISOString() : null }).eq('id', t.id);
    onUpdate();
  };

  const archiveGoal = async () => {
    await supabase.from('goals').update({ status: 'archived' }).eq('id', goal.id);
    onClose();
    onUpdate();
  };

  const deleteGoal = async () => {
    await supabase.from('milestones').delete().eq('goal_id', goal.id);
    await supabase.from('tasks').update({ goal_id: null, milestone_id: null }).eq('goal_id', goal.id);
    await supabase.from('goals').delete().eq('id', goal.id);
    onClose();
    onUpdate();
  };

  const deleteMilestone = async (mId) => {
    // Unlink tasks from this milestone (keep them on the goal)
    await supabase.from('tasks').update({ milestone_id: null }).eq('milestone_id', mId);
    await supabase.from('milestones').delete().eq('id', mId);
    onUpdate();
  };

  const addMilestone = async () => {
    if (!newMilestoneTitle.trim()) return;
    const nextOrder = goalMilestones.length + 1;
    await supabase.from('milestones').insert({
      goal_id: goal.id, title: newMilestoneTitle.trim(), done: false, sort_order: nextOrder,
    });
    setNewMilestoneTitle('');
    setAddingMilestone(false);
    onUpdate();
  };

  const saveMilestoneEdit = async (mId) => {
    if (!editMilestoneTitle.trim()) return;
    await supabase.from('milestones').update({ title: editMilestoneTitle.trim() }).eq('id', mId);
    setEditingMilestone(null);
    onUpdate();
  };

  const addTask = async (milestoneId = null) => {
    if (!newTaskTitle.trim()) return;
    const nextOrder = goalTasks.length + 1;
    await supabase.from('tasks').insert({
      goal_id: goal.id, milestone_id: milestoneId, role_id: goal.role_id || null,
      title: newTaskTitle.trim(), priority: 2, done: false, sort_order: nextOrder,
    });
    setNewTaskTitle('');
    setAddingTask(false);
    setNewTaskMilestoneId(null);
    onUpdate();
  };

  const deleteTask = async (tId) => {
    await supabase.from('tasks').delete().eq('id', tId);
    onUpdate();
  };

  const updateMilestoneDate = async (mId, date) => {
    await supabase.from('milestones').update({ target_date: date || null }).eq('id', mId);
    setEditingMilestoneDate(null);
    onUpdate();
  };

  const updateTaskDate = async (tId, date) => {
    await supabase.from('tasks').update({ due_date: date || null }).eq('id', tId);
    setEditingTaskDate(null);
    onUpdate();
  };

  const inputStyle = {
    width: '100%', padding: '8px 12px', borderRadius: 10, fontSize: 14,
    border: '1px solid rgba(0,0,0,0.12)', background: 'rgba(255,255,255,0.8)',
    fontFamily: 'inherit', color: T.text, outline: 'none',
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.15)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', zIndex: 100 }}
      />
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={springs.snappy}
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, width: 520,
          background: 'rgba(250,250,248,0.95)', backdropFilter: 'blur(40px) saturate(180%)',
          WebkitBackdropFilter: 'blur(40px) saturate(180%)',
          borderLeft: '1px solid rgba(255,255,255,0.8)',
          boxShadow: '-20px 0 60px rgba(0,0,0,0.08)',
          zIndex: 101, overflowY: 'auto',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 12,
          borderBottom: '1px solid rgba(0,0,0,0.06)',
          position: 'sticky', top: 0, background: 'rgba(250,250,248,0.92)',
          backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', zIndex: 1,
        }}>
          <RoleBadge roleSlug={goal.roles?.slug} />
          {h && <HealthBadge health={health} />}
          {(() => { const gp = GOAL_PRIORITY[goal.priority]; return gp && goal.priority <= 2 ? (
            <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: gp.bg, color: gp.color, border: `1px solid ${gp.border}` }}>{gp.label}</span>
          ) : null; })()}
          <div style={{ flex: 1 }} />
          {!isEditing ? (
            <Btn small variant="ghost" onClick={() => setIsEditing(true)}><Pencil size={12} /> Edit</Btn>
          ) : (
            <div style={{ display: 'flex', gap: 6 }}>
              <Btn small variant="ghost" onClick={cancelEdit}>Cancel</Btn>
              <button onClick={saveGoal} disabled={saving} style={{
                padding: '6px 14px', borderRadius: 8, border: 'none',
                background: T.amber, color: '#fff', cursor: 'pointer', fontSize: 12,
                fontWeight: 600, fontFamily: 'inherit', opacity: saving ? 0.6 : 1,
              }}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
          )}
          <button onClick={onClose} style={{
            width: 32, height: 32, borderRadius: 8, border: 'none',
            background: 'rgba(0,0,0,0.04)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <X size={16} color={T.textMuted} />
          </button>
        </div>

        <div style={{ padding: '24px 24px 32px' }}>
          {isEditing ? (
            <input value={editTitle} onChange={e => setEditTitle(e.target.value)} style={{ ...inputStyle, fontSize: 20, fontWeight: 700, padding: '10px 14px', marginBottom: 8, letterSpacing: '-0.02em' }} />
          ) : (
            <h2 style={{ fontSize: 20, fontWeight: 700, color: T.text, marginBottom: 8, lineHeight: 1.3, letterSpacing: '-0.02em' }}>{goal.title}</h2>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16 }}>
            <Calendar size={13} color={T.textMuted} />
            {isEditing ? (
              <input type="date" value={editDeadline} onChange={e => setEditDeadline(e.target.value)} style={{ ...inputStyle, width: 'auto', padding: '4px 10px', fontSize: 13 }} />
            ) : (
              <span style={{ fontSize: 13, color: goal.deadline ? T.textMid : T.textMuted }}>{goal.deadline ? fmtDate(goal.deadline) : 'No deadline'}</span>
            )}
          </div>

          {isEditing && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16 }}>
              <Flag size={13} color={T.textMuted} />
              <div style={{ display: 'flex', gap: 6 }}>
                {Object.entries(GOAL_PRIORITY).map(([val, p]) => (
                  <button key={val} onClick={() => setEditPriority(Number(val))} style={{
                    fontSize: 11, fontWeight: 500, padding: '3px 10px', borderRadius: 10, cursor: 'pointer',
                    fontFamily: 'inherit', border: `1px solid ${editPriority === Number(val) ? p.border : 'rgba(0,0,0,0.06)'}`,
                    background: editPriority === Number(val) ? p.bg : 'transparent',
                    color: editPriority === Number(val) ? p.color : T.textMuted,
                  }}>{p.label}</button>
                ))}
              </div>
            </div>
          )}

          {isEditing ? (
            <textarea value={editDesc} onChange={e => setEditDesc(e.target.value)} rows={3} placeholder="Add a description..." style={{ ...inputStyle, resize: 'vertical', marginBottom: 20, lineHeight: 1.5 }} />
          ) : goal.description ? (
            <Md style={{ fontSize: 13, marginBottom: 20, lineHeight: 1.6 }}>{goal.description}</Md>
          ) : (
            <div style={{ fontSize: 13, color: T.textLight, marginBottom: 20, fontStyle: 'italic' }}>No description</div>
          )}

          {/* Progress */}
          <div style={{ padding: 20, borderRadius: 14, marginBottom: 24, background: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.85)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Progress</span>
              <span style={{ fontSize: 22, fontWeight: 700, color: r?.color || T.amber }}>{progress}%</span>
            </div>
            <ProgressBar value={progress} color={r?.color || T.amber} height={6} />
            {goal.type === 'quantitative' && (
              <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: T.textMuted }}>{goal.metric_label}</span>
                {isEditing ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontSize: 13, color: T.textMuted }}>{goal.metric_unit}</span>
                    <input type="number" value={editMetric} onChange={e => setEditMetric(e.target.value)} style={{ ...inputStyle, width: 90, padding: '4px 8px', fontSize: 13, textAlign: 'right' }} />
                    <span style={{ fontSize: 13, color: T.textMuted }}>/ {goal.metric_unit}{fmt(goal.metric_target)}</span>
                  </div>
                ) : (
                  <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{goal.metric_unit}{fmt(goal.metric_current)} / {goal.metric_unit}{fmt(goal.metric_target)}</span>
                )}
              </div>
            )}
          </div>

          {/* Milestones + Nested Tasks */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <Flag size={13} color={r?.color || T.amber} />
              <span style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Milestones</span>
              <span style={{ fontSize: 11, color: T.textMuted }}>{doneMilestones}/{goalMilestones.length}</span>
              <button onClick={() => setAddingMilestone(true)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: r?.color || T.amber, fontSize: 11, fontWeight: 600, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4 }}>
                <Plus size={11} /> Add
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {goalMilestones.map((m, idx) => {
                const mTasks = (tasksByMilestone[m.id] || []).sort((a, b) => (a.done ? 1 : 0) - (b.done ? 1 : 0) || a.sort_order - b.sort_order);
                return (
                  <div key={m.id} style={{ borderRadius: 10, border: '1px solid rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                    {/* Milestone header */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                      background: m.done ? 'rgba(90,158,120,0.04)' : 'rgba(255,255,255,0.5)',
                    }}>
                      <button onClick={() => toggleMilestone(m)} style={{
                        width: 20, height: 20, borderRadius: 6, flexShrink: 0,
                        border: `1.5px solid ${m.done ? T.green : 'rgba(0,0,0,0.15)'}`,
                        background: m.done ? T.greenLight : 'transparent',
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {m.done && <Check size={12} color={T.green} />}
                      </button>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {editingMilestone === m.id ? (
                          <input value={editMilestoneTitle} onChange={e => setEditMilestoneTitle(e.target.value)}
                            onBlur={() => saveMilestoneEdit(m.id)}
                            onKeyDown={e => { if (e.key === 'Enter') saveMilestoneEdit(m.id); if (e.key === 'Escape') setEditingMilestone(null); }}
                            autoFocus style={{ ...inputStyle, padding: '4px 8px', fontSize: 13 }} />
                        ) : (
                          <div onClick={() => { setEditingMilestone(m.id); setEditMilestoneTitle(m.title); }} style={{ fontSize: 13, fontWeight: 500, color: m.done ? T.textMuted : T.text, textDecoration: m.done ? 'line-through' : 'none', cursor: 'pointer' }}>
                            {m.title}
                          </div>
                        )}
                        {m.rationale && <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>{m.rationale}</div>}
                      </div>
                      {editingMilestoneDate === m.id ? (
                        <input type="date" defaultValue={m.target_date?.split('T')[0] || ''} autoFocus
                          onBlur={e => updateMilestoneDate(m.id, e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') { updateMilestoneDate(m.id, e.target.value); } if (e.key === 'Escape') setEditingMilestoneDate(null); }}
                          style={{ ...inputStyle, width: 'auto', padding: '2px 6px', fontSize: 10 }} />
                      ) : (
                        <span onClick={() => setEditingMilestoneDate(m.id)} style={{ fontSize: 10, color: m.target_date ? T.textMuted : T.textLight, flexShrink: 0, cursor: 'pointer', padding: '2px 4px', borderRadius: 4 }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.04)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                          title="Click to edit date">
                          {m.target_date ? new Date(m.target_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : 'Set date'}
                        </span>
                      )}
                      <span style={{ fontSize: 10, color: T.textLight }}>{mTasks.length}t</span>
                      <button onClick={() => deleteMilestone(m.id)} style={{ padding: 2, border: 'none', background: 'none', cursor: 'pointer', opacity: 0.3 }}><X size={11} color={T.textMuted} /></button>
                    </div>
                    {/* Nested tasks */}
                    {mTasks.length > 0 && (
                      <div style={{ padding: '4px 14px 8px 46px', background: 'rgba(0,0,0,0.01)' }}>
                        {mTasks.map(t => (
                          <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', opacity: t.done ? 0.5 : 1 }}>
                            <button onClick={() => toggleTask(t)} style={{
                              width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                              border: `1.5px solid ${t.done ? T.green : 'rgba(0,0,0,0.12)'}`,
                              background: t.done ? T.greenLight : 'transparent',
                              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>{t.done && <Check size={10} color={T.green} />}</button>
                            <span style={{ fontSize: 12, color: T.text, flex: 1, textDecoration: t.done ? 'line-through' : 'none' }}>{t.title}</span>
                            {t.priority === 1 && !t.done && <div style={{ width: 5, height: 5, borderRadius: '50%', background: T.red }} />}
                            {editingTaskDate === t.id ? (
                              <input type="date" defaultValue={t.due_date || ''} autoFocus
                                onBlur={e => updateTaskDate(t.id, e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') updateTaskDate(t.id, e.target.value); if (e.key === 'Escape') setEditingTaskDate(null); }}
                                style={{ ...inputStyle, width: 'auto', padding: '1px 4px', fontSize: 10 }} />
                            ) : (
                              <span onClick={() => setEditingTaskDate(t.id)} style={{ fontSize: 10, color: t.due_date ? T.textMuted : T.textLight, cursor: 'pointer', padding: '1px 4px', borderRadius: 4 }}
                                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.04)'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                                title="Click to set due date">
                                {t.due_date ? new Date(t.due_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''}
                              </span>
                            )}
                            {t.effort && <span style={{ fontSize: 10, color: T.textLight }}>{t.effort}</span>}
                            <button onClick={() => deleteTask(t.id)} style={{ padding: 1, border: 'none', background: 'none', cursor: 'pointer', opacity: 0.3 }}><X size={10} color={T.textMuted} /></button>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Add task to milestone */}
                    {addingTask && newTaskMilestoneId === m.id ? (
                      <div style={{ padding: '6px 14px 8px 46px' }}>
                        <input value={newTaskTitle} onChange={e => setNewTaskTitle(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') addTask(m.id); if (e.key === 'Escape') { setAddingTask(false); setNewTaskMilestoneId(null); } }}
                          placeholder="New task..." autoFocus
                          style={{ ...inputStyle, padding: '4px 8px', fontSize: 12 }} />
                      </div>
                    ) : (
                      <button onClick={() => { setAddingTask(true); setNewTaskMilestoneId(m.id); setNewTaskTitle(''); }}
                        style={{ display: 'block', width: '100%', padding: '4px 14px 6px 46px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: T.textLight, fontFamily: 'inherit', textAlign: 'left' }}>
                        + Add task
                      </button>
                    )}
                  </div>
                );
              })}
              {/* Add milestone form */}
              {addingMilestone && (
                <div style={{ padding: '8px 14px', borderRadius: 10, border: '1px solid rgba(0,0,0,0.08)', background: 'rgba(255,255,255,0.5)' }}>
                  <input value={newMilestoneTitle} onChange={e => setNewMilestoneTitle(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') addMilestone(); if (e.key === 'Escape') setAddingMilestone(false); }}
                    placeholder="New milestone..." autoFocus
                    style={{ ...inputStyle, padding: '6px 10px', fontSize: 13 }} />
                </div>
              )}
            </div>
          </div>

          {/* Orphan Tasks (not under any milestone) */}
          {orphanTasks.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <ListChecks size={13} color={r?.color || T.amber} />
                <span style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Tasks</span>
                <span style={{ fontSize: 11, color: T.textMuted }}>{orphanTasks.filter(t => t.done).length}/{orphanTasks.length}</span>
                <button onClick={() => { setAddingTask(true); setNewTaskMilestoneId(null); setNewTaskTitle(''); }} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: r?.color || T.amber, fontSize: 11, fontWeight: 600, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Plus size={11} /> Add
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {orphanTasks.sort((a, b) => (a.done ? 1 : 0) - (b.done ? 1 : 0) || a.sort_order - b.sort_order).map(t => (
                  <div key={t.id} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8,
                    background: t.done ? 'transparent' : 'rgba(255,255,255,0.5)', border: '1px solid rgba(0,0,0,0.04)',
                    opacity: t.done ? 0.5 : 1,
                  }}>
                    <button onClick={() => toggleTask(t)} style={{
                      width: 18, height: 18, borderRadius: 5, flexShrink: 0,
                      border: `1.5px solid ${t.done ? T.green : 'rgba(0,0,0,0.15)'}`,
                      background: t.done ? T.greenLight : 'transparent',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>{t.done && <Check size={11} color={T.green} />}</button>
                    <span style={{ fontSize: 13, fontWeight: 500, color: T.text, flex: 1, textDecoration: t.done ? 'line-through' : 'none' }}>{t.title}</span>
                    {t.priority === 1 && !t.done && <div style={{ width: 6, height: 6, borderRadius: '50%', background: T.red, flexShrink: 0 }} />}
                    {editingTaskDate === t.id ? (
                      <input type="date" defaultValue={t.due_date || ''} autoFocus
                        onBlur={e => updateTaskDate(t.id, e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') updateTaskDate(t.id, e.target.value); if (e.key === 'Escape') setEditingTaskDate(null); }}
                        style={{ ...inputStyle, width: 'auto', padding: '2px 6px', fontSize: 11 }} />
                    ) : (
                      <span onClick={() => setEditingTaskDate(t.id)} style={{ fontSize: 11, color: t.due_date ? T.textMuted : T.textLight, cursor: 'pointer', padding: '2px 4px', borderRadius: 4, flexShrink: 0 }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.04)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                        title="Click to set due date">
                        {t.due_date ? new Date(t.due_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''}
                      </span>
                    )}
                    {t.effort && <span style={{ fontSize: 11, color: T.textMuted, flexShrink: 0 }}>{t.effort}</span>}
                    <button onClick={() => deleteTask(t.id)} style={{ padding: 2, border: 'none', background: 'none', cursor: 'pointer', opacity: 0.3 }}><X size={10} color={T.textMuted} /></button>
                  </div>
                ))}
                {/* Add orphan task form */}
                {addingTask && newTaskMilestoneId === null && (
                  <div style={{ padding: '4px 0' }}>
                    <input value={newTaskTitle} onChange={e => setNewTaskTitle(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') addTask(null); if (e.key === 'Escape') setAddingTask(false); }}
                      placeholder="New task..." autoFocus
                      style={{ ...inputStyle, padding: '6px 10px', fontSize: 13 }} />
                  </div>
                )}
              </div>
            </div>
          )}
          {/* Add task button when no orphan tasks and no milestones */}
          {orphanTasks.length === 0 && goalMilestones.length === 0 && (
            <div style={{ marginBottom: 24 }}>
              {addingTask ? (
                <input value={newTaskTitle} onChange={e => setNewTaskTitle(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') addTask(null); if (e.key === 'Escape') setAddingTask(false); }}
                  placeholder="New task..." autoFocus
                  style={{ ...inputStyle, padding: '8px 12px', fontSize: 13 }} />
              ) : (
                <button onClick={() => { setAddingTask(true); setNewTaskMilestoneId(null); setNewTaskTitle(''); }} style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, border: '1px dashed rgba(0,0,0,0.12)',
                  background: 'transparent', color: T.textMuted, cursor: 'pointer', fontSize: 12, fontWeight: 500, fontFamily: 'inherit', width: '100%',
                }}><Plus size={12} /> Add task</button>
              )}
            </div>
          )}

          {/* Actions */}
          <div style={{ paddingTop: 20, borderTop: '1px solid rgba(0,0,0,0.06)', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Btn variant="ghost" onClick={() => { navigator.clipboard.writeText('claude < ~/exec-hq/scripts/new-goal.md'); setCopied(true); setTimeout(() => setCopied(false), 2000); }}>
              {copied ? <><Check size={13} color={T.green} /> Copied</> : <><Copy size={13} /> Re-plan with AI</>}
            </Btn>
            <Btn variant="danger" onClick={archiveGoal}><Archive size={13} /> Archive</Btn>
            <Btn variant="danger" onClick={deleteGoal}><Trash2 size={13} /> Delete</Btn>
          </div>
        </div>
      </motion.div>
    </>
  );
};

// Portfolio health bar
const PortfolioHealth = ({ goals, milestones }) => {
  const active = goals.filter(g => g.status === 'active');
  if (active.length === 0) return null;

  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '8px 0' }}>
      {active.map(goal => {
        const health = goalHealth(goal, milestones);
        const h = health ? HEALTH[health] : null;
        return (
          <div key={goal.id} title={`${goal.title}: ${h?.label || 'Unknown'}`} style={{
            width: 10, height: 10, borderRadius: '50%',
            background: h?.color || T.textLight,
            border: `1px solid ${h?.border || 'rgba(0,0,0,0.08)'}`,
            transition: 'all 0.3s',
          }} />
        );
      })}
      <span style={{ fontSize: 11, color: T.textMuted, marginLeft: 4 }}>
        {active.filter(g => goalHealth(g, milestones) === 'on-track' || goalHealth(g, milestones) === 'done').length}/{active.length} on track
      </span>
    </div>
  );
};

// ─── New Goal Dialog ────────────────────────────────────────────────────────
const inputStyle = {
  width: '100%', padding: '10px 14px', borderRadius: 10, fontSize: 13,
  border: '1px solid rgba(0,0,0,0.10)', background: 'rgba(255,255,255,0.8)',
  fontFamily: 'inherit', color: T.text, outline: 'none',
};

const NewGoalDialog = ({ onClose, onUpdate }) => {
  // Step 1: Input
  const [step, setStep] = useState(1);
  const [title, setTitle] = useState('');
  const [roleSlug, setRoleSlug] = useState('liken');
  const [priority, setPriority] = useState(2);
  const [type, setType] = useState('qualitative');
  const [metricLabel, setMetricLabel] = useState('');
  const [metricUnit, setMetricUnit] = useState('');
  const [metricCurrent, setMetricCurrent] = useState('0');
  const [metricTarget, setMetricTarget] = useState('');
  const [deadline, setDeadline] = useState('');
  const [userContext, setUserContext] = useState('');

  // Step 2: AI breakdown
  const [generating, setGenerating] = useState(false);
  const [breakdown, setBreakdown] = useState(null);
  const [error, setError] = useState('');

  // Step 3: Save
  const [saving, setSaving] = useState(false);

  const generate = async () => {
    if (!title.trim()) return;
    setGenerating(true);
    setError('');
    try {
      const res = await apiFetch('/goals/generate', {
        method: 'POST',
        body: JSON.stringify({
          title: title.trim(), role_slug: roleSlug, priority, type,
          metric_label: metricLabel || null, metric_unit: metricUnit || null,
          metric_current: Number(metricCurrent) || 0, metric_target: Number(metricTarget) || 0,
          deadline: deadline || null, context: userContext || null,
        }),
      });
      if (!res.ok) throw new Error('Generation failed');
      const data = await res.json();
      setBreakdown(data);
      setStep(2);
    } catch (err) {
      setError('Failed to generate breakdown. Try again.');
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await apiFetch('/goals/save', {
        method: 'POST',
        body: JSON.stringify({
          title: title.trim(), description: breakdown?.description || null,
          role_slug: roleSlug, priority, type,
          metric_label: metricLabel || null, metric_unit: metricUnit || null,
          metric_current: Number(metricCurrent) || 0, metric_target: Number(metricTarget) || 0,
          deadline: deadline || null,
          milestones: breakdown?.milestones || [],
          tasks: breakdown?.tasks || [],
        }),
      });
      if (!res.ok) throw new Error('Save failed');
      onUpdate();
      onClose();
    } catch (err) {
      setError('Failed to save goal. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const removeMilestone = (idx) => {
    setBreakdown(prev => ({ ...prev, milestones: prev.milestones.filter((_, i) => i !== idx) }));
  };

  const removeTask = (idx) => {
    setBreakdown(prev => ({ ...prev, tasks: prev.tasks.filter((_, i) => i !== idx) }));
  };

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.15)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', zIndex: 100 }} />
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 101, pointerEvents: 'none',
      }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
          transition={springs.gentle}
          style={{
            width: step === 1 ? 480 : 600, maxHeight: '85vh', overflowY: 'auto', pointerEvents: 'auto',
          }}
        >
        <Glass depth={3} style={{ padding: 28 }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: T.amberLight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Target size={16} color={T.amber} />
              </div>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: T.text }}>
                {step === 1 ? 'New Goal' : 'Review Breakdown'}
              </h3>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
              <X size={16} color={T.textMuted} />
            </button>
          </div>

          {/* Step 1: Input */}
          {step === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: T.textMuted, display: 'block', marginBottom: 6 }}>What's the goal?</label>
                <input style={inputStyle} value={title} onChange={e => setTitle(e.target.value)}
                  placeholder="e.g. Close £500k pre-seed round for Liken" autoFocus
                  onKeyDown={e => { if (e.key === 'Enter' && title.trim()) generate(); }} />
              </div>

              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: T.textMuted, display: 'block', marginBottom: 6 }}>Role</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {Object.entries(ROLES).map(([slug, r]) => (
                    <Pill key={slug} active={roleSlug === slug} color={r.color} bg={r.light} border={r.border} onClick={() => setRoleSlug(slug)}>
                      {r.label}
                    </Pill>
                  ))}
                </div>
              </div>

              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: T.textMuted, display: 'block', marginBottom: 6 }}>Priority</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {Object.entries(GOAL_PRIORITY).map(([val, p]) => (
                    <Pill key={val} active={priority === Number(val)} color={p.color} bg={p.bg} border={p.border} onClick={() => setPriority(Number(val))}>
                      {p.label}
                    </Pill>
                  ))}
                </div>
              </div>

              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: T.textMuted, display: 'block', marginBottom: 6 }}>Type</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Pill active={type === 'qualitative'} onClick={() => setType('qualitative')}>
                    <ListChecks size={12} /> Milestone-based
                  </Pill>
                  <Pill active={type === 'quantitative'} onClick={() => setType('quantitative')}>
                    <Target size={12} /> Quantitative
                  </Pill>
                </div>
              </div>

              {type === 'quantitative' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px 80px', gap: 8 }}>
                  <input style={inputStyle} placeholder="Metric (e.g. Creators onboarded)" value={metricLabel} onChange={e => setMetricLabel(e.target.value)} />
                  <input style={inputStyle} placeholder="Unit" value={metricUnit} onChange={e => setMetricUnit(e.target.value)} />
                  <input style={inputStyle} placeholder="Current" type="number" value={metricCurrent} onChange={e => setMetricCurrent(e.target.value)} />
                  <input style={inputStyle} placeholder="Target" type="number" value={metricTarget} onChange={e => setMetricTarget(e.target.value)} />
                </div>
              )}

              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: T.textMuted, display: 'block', marginBottom: 6 }}>Deadline</label>
                <input style={{ ...inputStyle, width: 200 }} type="date" value={deadline} onChange={e => setDeadline(e.target.value)} />
              </div>

              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: T.textMuted, display: 'block', marginBottom: 6 }}>Context (optional)</label>
                <textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} value={userContext} onChange={e => setUserContext(e.target.value)}
                  placeholder="Dependencies, constraints, co-founders involved, what success looks like..." />
              </div>

              {error && <div style={{ fontSize: 13, color: '#dc2626' }}>{error}</div>}

              <button onClick={generate} disabled={!title.trim() || generating} style={{
                padding: '10px 20px', borderRadius: 10, border: 'none', cursor: 'pointer',
                background: T.amber, color: '#fff', fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                opacity: !title.trim() || generating ? 0.5 : 1, marginTop: 4,
              }}>
                {generating ? <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Sparkles size={14} />}
                {generating ? 'Generating breakdown...' : 'Generate with AI'}
              </button>
            </div>
          )}

          {/* Step 2: Review breakdown */}
          {step === 2 && breakdown && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Description */}
              {breakdown.description && (
                <div style={{ fontSize: 13, color: T.textMid, lineHeight: 1.5, padding: 12, background: 'rgba(0,0,0,0.02)', borderRadius: 8 }}>
                  {breakdown.description}
                </div>
              )}

              {/* Milestones */}
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: T.textMuted, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 10 }}>
                  Milestones ({breakdown.milestones?.length || 0})
                </div>
                {breakdown.milestones?.map((m, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 0',
                    borderBottom: i < breakdown.milestones.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none',
                  }}>
                    <div style={{ width: 20, height: 20, borderRadius: '50%', border: '2px solid rgba(0,0,0,0.15)', flexShrink: 0, marginTop: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: T.textMuted, fontWeight: 600 }}>
                      {i + 1}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: T.text }}>{m.title}</div>
                      {m.rationale && <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>{m.rationale}</div>}
                      {m.target_date && <div style={{ fontSize: 11, color: T.textLight, marginTop: 2 }}>{m.target_date}</div>}
                    </div>
                    <button onClick={() => removeMilestone(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: T.textLight }}>
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>

              {/* Tasks */}
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: T.textMuted, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 10 }}>
                  Tasks ({breakdown.tasks?.length || 0})
                </div>
                {breakdown.tasks?.map((t, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0',
                    borderBottom: i < breakdown.tasks.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none',
                  }}>
                    <span style={{
                      fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 6,
                      background: t.priority === 1 ? 'rgba(220,38,38,0.08)' : 'rgba(0,0,0,0.04)',
                      color: t.priority === 1 ? '#dc2626' : T.textMuted,
                    }}>P{t.priority}</span>
                    <span style={{ fontSize: 13, color: T.text, flex: 1 }}>{t.title}</span>
                    {t.effort && <span style={{ fontSize: 11, color: T.textLight }}>{t.effort}</span>}
                    <button onClick={() => removeTask(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: T.textLight }}>
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>

              {/* Strategic note */}
              {breakdown.strategic_note && (
                <div style={{ padding: 12, background: 'rgba(212,146,30,0.05)', border: '1px solid rgba(212,146,30,0.15)', borderRadius: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: T.amber, marginBottom: 4 }}>Strategic Note</div>
                  <div style={{ fontSize: 13, color: T.textMid, lineHeight: 1.5 }}>{breakdown.strategic_note}</div>
                </div>
              )}

              {error && <div style={{ fontSize: 13, color: '#dc2626' }}>{error}</div>}

              {/* Actions */}
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <button onClick={save} disabled={saving} style={{
                  padding: '10px 24px', borderRadius: 10, border: 'none', cursor: 'pointer',
                  background: '#22c55e', color: '#fff', fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                  display: 'flex', alignItems: 'center', gap: 6, opacity: saving ? 0.5 : 1,
                }}>
                  <Check size={14} /> {saving ? 'Saving...' : 'Save Goal'}
                </button>
                <button onClick={() => { setStep(1); setBreakdown(null); }} style={{
                  padding: '10px 16px', borderRadius: 10, border: '1px solid rgba(0,0,0,0.1)',
                  background: 'transparent', color: T.textMid, fontSize: 13, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer',
                }}>Back</button>
                <button onClick={generate} disabled={generating} style={{
                  padding: '10px 16px', borderRadius: 10, border: '1px solid rgba(0,0,0,0.1)',
                  background: 'transparent', color: T.textMid, fontSize: 13, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6, opacity: generating ? 0.5 : 1,
                }}>
                  <RefreshCw size={12} /> Regenerate
                </button>
              </div>
            </div>
          )}
        </Glass>
      </motion.div>
      </div>
    </>
  );
};

export const GoalsView = ({ goals, milestones, tasks, onUpdate }) => {
  const [filter, setFilter] = useState('all');
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [showNewGoal, setShowNewGoal] = useState(false);
  const filtered = filter === 'all' ? goals : goals.filter(g => g.roles?.slug === filter);
  const activeGoal = selectedGoal ? goals.find(g => g.id === selectedGoal.id) : null;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={springs.gentle}>
      {(() => {
        const active = goals.filter(g => g.status === 'active');
        const onTrack = active.filter(g => goalHealth(g, milestones || []) === 'on-track').length;
        const atRisk = active.filter(g => {
          const h = goalHealth(g, milestones || []);
          return h === 'at-risk' || h === 'overdue';
        }).length;
        return (
          <ViewHero
            icon={Target}
            gradient="linear-gradient(135deg, #8FD4A6 0%, #5A9E78 100%)"
            title="Goals"
            stats={[
              { label: `${active.length} active`, color: T.green, bg: 'rgba(90,158,120,0.10)', border: 'rgba(90,158,120,0.18)' },
              { label: `${onTrack} on track`, color: T.green, bg: 'rgba(90,158,120,0.10)', border: 'rgba(90,158,120,0.18)' },
              { label: `${atRisk} at risk`, color: atRisk > 0 ? T.red : T.textMuted, bg: atRisk > 0 ? 'rgba(224,88,88,0.10)' : 'rgba(0,0,0,0.04)', border: atRisk > 0 ? 'rgba(224,88,88,0.18)' : 'rgba(0,0,0,0.05)' },
            ]}
            right={<Btn variant="primary" onClick={() => setShowNewGoal(true)}><Plus size={14} />New Goal</Btn>}
          />
        );
      })()}

      <PortfolioHealth goals={goals} milestones={milestones} />

      <div style={{ display: 'flex', gap: 8, marginBottom: 24, marginTop: 8 }}>
        {['all', ...Object.keys(ROLES)].map(f => {
          const r = ROLES[f];
          const count = f === 'all' ? goals.length : goals.filter(g => g.roles?.slug === f).length;
          return (
            <Pill key={f} active={filter === f} color={r?.color} bg={r?.light} border={r?.border} onClick={() => setFilter(f)}>
              {f === 'all' ? 'All' : r.label}
              {count > 0 && <span style={{ marginLeft: 5, opacity: 0.6, fontSize: 11 }}>{count}</span>}
            </Pill>
          );
        })}
      </div>

      <motion.div layout style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: 20 }}>
        <AnimatePresence>
          {filtered.map(goal => {
            const r = ROLES[goal.roles?.slug];
            const goalMilestones = milestones.filter(m => m.goal_id === goal.id);
            const doneMilestones = goalMilestones.filter(m => m.done).length;
            const goalTasks = tasks.filter(t => t.goal_id === goal.id && !t.done);
            const progress = goal.type === 'quantitative'
              ? pct(goal.metric_current, goal.metric_target)
              : goalMilestones.length > 0 ? Math.round((doneMilestones / goalMilestones.length) * 100) : 0;
            const nextMilestone = goalMilestones.find(m => !m.done);
            const health = goalHealth(goal, milestones);
            const h = health ? HEALTH[health] : null;

            return (
              <motion.div key={goal.id} layout initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.97 }} transition={springs.gentle}>
                <Glass onClick={() => setSelectedGoal(goal)} style={{ padding: 24, cursor: 'pointer' }} tint={goal.roles?.slug}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <RoleBadge roleSlug={goal.roles?.slug} />
                      {h && <HealthBadge health={health} small />}
                      {(() => { const gp = GOAL_PRIORITY[goal.priority]; return gp && goal.priority <= 2 ? (
                        <span style={{ fontSize: 9, fontWeight: 600, padding: '1px 6px', borderRadius: 8, background: gp.bg, color: gp.color }}>{gp.label}</span>
                      ) : null; })()}
                    </div>
                    {goal.deadline && <span style={{ fontSize: 11, color: health === 'overdue' ? T.red : T.textMuted, fontWeight: health === 'overdue' ? 600 : 400, background: 'rgba(0,0,0,0.04)', padding: '2px 8px', borderRadius: 10 }}>{fmtDeadline(goal.deadline)}</span>}
                  </div>

                  <h3 style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 6, lineHeight: 1.3 }}>{goal.title}</h3>
                  {goal.description && (
                    <div style={{ fontSize: 13, color: T.textMid, marginBottom: 14, lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{goal.description}</div>
                  )}

                  <div style={{ marginBottom: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ fontSize: 12, color: T.textMuted }}>{goal.type === 'quantitative' ? goal.metric_label : `${doneMilestones}/${goalMilestones.length} milestones`}</span>
                      <span style={{ fontSize: 12, color: r?.color || T.amber, fontWeight: 600 }}>{progress}%</span>
                    </div>
                    <ProgressBar value={progress} color={r?.color || T.amber} height={5} />
                  </div>

                  {goalMilestones.length > 0 && (
                    <div style={{ display: 'flex', gap: 4, marginBottom: 14, alignItems: 'center' }}>
                      {goalMilestones.map(m => (
                        <div key={m.id} title={m.title} style={{ width: 8, height: 8, borderRadius: '50%', background: m.done ? (r?.color || T.amber) : 'rgba(0,0,0,0.08)' }} />
                      ))}
                      {nextMilestone && <span style={{ fontSize: 11, color: T.textMuted, marginLeft: 6 }}>Next: {nextMilestone.title}</span>}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 16, paddingTop: 12, borderTop: '1px solid rgba(0,0,0,0.06)', alignItems: 'center' }}>
                    <span style={{ fontSize: 12, color: T.textMuted }}><span style={{ color: T.text, fontWeight: 500 }}>{goalTasks.length}</span> open tasks</span>
                    <span style={{ marginLeft: 'auto', fontSize: 12, color: r?.color || T.textMuted, display: 'flex', alignItems: 'center', gap: 4, fontWeight: 500 }}>Open <ChevronRight size={12} /></span>
                  </div>
                </Glass>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </motion.div>

      <AnimatePresence>
        {activeGoal && (
          <GoalDetailPanel goal={activeGoal} milestones={milestones} tasks={tasks} onClose={() => setSelectedGoal(null)} onUpdate={onUpdate} />
        )}
        {showNewGoal && (
          <NewGoalDialog onClose={() => setShowNewGoal(false)} onUpdate={onUpdate} />
        )}
      </AnimatePresence>
    </motion.div>
  );
};
